import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Play, Info, Star, Clock, Sparkles, Loader2, AlertCircle, Plus, Eye, Film, Tv } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import WatchlistButton from './WatchlistButton';
import { useWatchlist } from '../hooks/useWatchlist';
import { getVideos, getContentTypes } from '../services/supabaseService';

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // État simple pour toutes les vidéos
  const [allVideos, setAllVideos] = useState([]);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  
  const { user, userProfile } = useAuth();
  const { watchlistCount } = useWatchlist();
  const navigate = useNavigate();

  // Utilitaire pour formater la durée
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Utilitaire pour obtenir la meilleure image
  const getBestImage = (video) => {
    return video.poster_url || video.thumbnail_url || '/placeholder-video.jpg';
  };

  // Récupérer TOUTES les vidéos de TOUS les utilisateurs (comme Netflix)
  const loadRealData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 DEBUT: Chargement de TOUTES les vidéos depuis Supabase direct...');

      const response = await getVideos({ limit: 50, skip: 0 });

      console.log('📡 Réponse Supabase:', response);

      if (response.success) {
        const videos = response.data || [];
        console.log(`✅ SUCCÈS: ${videos.length} vidéos récupérées depuis Supabase`);
        
        setAllVideos(videos);
        
        // Prendre la première vidéo comme vedette
        if (videos.length > 0) {
          console.log('🎬 Vidéo en vedette choisie:', videos[0].title, videos[0].id);
          setFeaturedVideo(videos[0]);
        }
        
      } else {
        console.error('❌ Erreur Supabase:', response.error);
        throw new Error(response.error || 'Erreur Supabase');
      }

    } catch (error) {
      console.error('❌ ERREUR chargement données:', error);
      setError(`Erreur: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Ajout du chargement des content types pour les filtres par type
  const [contentTypes, setContentTypes] = useState([]);
  
  const loadContentTypes = async () => {
    try {
      const response = await getContentTypes();
      if (response.success) {
        setContentTypes(response.data);
        console.log('🎯 Content types chargés:', response.data.map(t => `${t.slug}: ${t.id}`));
      }
    } catch (error) {
      console.error('Erreur chargement content types:', error);
    }
  };

  useEffect(() => {
    loadRealData();
    loadContentTypes();
  }, []);

  // Fonction pour regarder une vidéo
  const watchVideo = (video) => {
    if (video.playback_url) {
      // Si playback_url existe, la vidéo est prête même si status = "processing"
      console.log('🎬 Lecture vidéo:', video.title, video.playback_url);
      navigate(`/watch/${video.id}`);
    } else {
      console.warn('Vidéo pas encore prête - pas d\'URL de lecture');
      alert(`Cette vidéo est encore en traitement. Statut: ${video.status}. Aucune URL de lecture disponible.`);
    }
  };

  // Fonction pour voir les détails
  const viewDetails = (video) => {
    navigate(`/details/${video.id}`);
  };

  // Composant carte vidéo simplifié
  const VideoCard = ({ video }) => (
    <div 
      className="group relative bg-gray-800 rounded-xl overflow-hidden transform hover:scale-105 transition-all duration-300 cursor-pointer w-48 aspect-[2/3] flex-shrink-0"
      onClick={() => viewDetails(video)}
    >
      <img
        src={getBestImage(video)}
        alt={video.title}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.src = '/placeholder-video.jpg';
        }}
      />
      
      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      
      {/* Contenu overlay */}
      <div className="absolute inset-0 p-4 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="space-y-2">
          <h3 className="text-white font-semibold text-sm line-clamp-2">{video.title}</h3>
          
          <div className="flex items-center space-x-2 text-xs text-gray-300">
            {video.release_year && <span>{video.release_year}</span>}
            {video.duration_seconds && (
              <>
                <span>•</span>
                <div className="flex items-center space-x-1">
                  <Clock className="h-3 w-3" />
                  <span>{formatDuration(video.duration_seconds)}</span>
                </div>
              </>
            )}
          </div>

          {video.created_by && (
            <p className="text-xs text-gray-400 line-clamp-1">
              Par {video.created_by}
            </p>
          )}

          {/* Boutons d'action */}
          <div className="flex space-x-2 pt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                watchVideo(video);
              }}
              className="bg-pink-600 hover:bg-pink-700 text-white px-3 py-1 rounded-lg text-xs font-medium flex items-center space-x-1"
            >
              <Play className="h-3 w-3" />
              <span>Regarder</span>
            </button>
            <WatchlistButton videoId={video.id} size="small" />
          </div>
        </div>
      </div>

      {/* Badge statut */}
      <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-lg">
        <span className={`text-xs font-medium ${
          video.status === 'published' ? 'text-green-400' : 
          video.status === 'processing' ? 'text-yellow-400' : 
          'text-gray-400'
        }`}>
          {video.status === 'published' ? 'Prêt' : 
           video.status === 'processing' ? 'Traitement...' : 
           video.status}
        </span>
      </div>
    </div>
  );

  // Composant section
  const Section = ({ title, icon: Icon, videos, emptyMessage }) => {
    if (videos.length === 0) {
      return (
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-6">
            {Icon && <Icon className="h-6 w-6 text-pink-400" />}
            <h2 className="text-2xl font-bold text-white">{title}</h2>
          </div>
          <div className="text-center py-8">
            <p className="text-gray-400">{emptyMessage}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-6">
          {Icon && <Icon className="h-6 w-6 text-pink-400" />}
          <h2 className="text-2xl font-bold text-white">{title}</h2>
        </div>
        
        <div className="flex space-x-4 overflow-x-auto scrollbar-hide pb-4">
          {videos.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-pink-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Chargement des vraies données depuis Supabase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-white text-lg mb-2">Erreur de chargement</p>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={loadRealData}
            className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-3 rounded-lg font-medium"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Préparer les 4 sections Netflix avec les vraies données
  
  // 🔥 Tendances actuelles - toutes les vidéos triées par vues ou date
  const trendingVideos = [...allVideos]
    .sort((a, b) => {
      // Trier par views_count si disponible, sinon par created_at
      if (a.views_count && b.views_count) {
        return b.views_count - a.views_count;
      }
      return new Date(b.created_at) - new Date(a.created_at);
    })
    .slice(0, 10);

  // 🎬 Films IA populaires - vidéos de type "movie"
  const movieTypeId = contentTypes.find(t => t.slug === 'movie' || t.slug === 'movies')?.id;
  const popularMovies = [...allVideos]
    .filter(v => movieTypeId ? v.content_type_id === movieTypeId : true) // Afficher toutes si pas de type movie trouvé
    .sort((a, b) => {
      // Trier par avg_rating si disponible, mettre les null à la fin
      if (a.avg_rating && b.avg_rating) {
        return b.avg_rating - a.avg_rating;
      }
      if (a.avg_rating && !b.avg_rating) return -1;
      if (!a.avg_rating && b.avg_rating) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    })
    .slice(0, 10);

  // 📺 Séries IA exclusives - vidéos de type série/anime/manga
  const seriesTypeIds = contentTypes
    .filter(t => ['series', 'anime', 'manga', 'tv'].includes(t.slug))
    .map(t => t.id);
  const exclusiveSeries = [...allVideos]
    .filter(v => seriesTypeIds.length > 0 ? seriesTypeIds.includes(v.content_type_id) : false)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 10);

  // ✨ Nouveautés - vidéos des 30 derniers jours
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const newReleases = [...allVideos]
    .filter(v => new Date(v.created_at) > thirtyDaysAgo)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 10);

  // Si pas de nouveautés dans les 30 jours, prendre les plus récentes
  const displayNewReleases = newReleases.length > 0 
    ? newReleases 
    : [...allVideos].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section avec image de fond futuriste fixe */}
      {featuredVideo && (
        <div className="relative h-screen">
          <div className="absolute inset-0">
            {/* Image de fond futuriste fixe - style Neural Nexus amélioré */}
            <div className="w-full h-full bg-gradient-to-br from-gray-900 via-black to-purple-900 relative overflow-hidden">
              {/* Image de fond futuriste alternative - plus visible */}
              <div 
                className="absolute inset-0 opacity-40"
                style={{
                  backgroundImage: `radial-gradient(circle at 60% 50%, rgba(124, 58, 237, 0.3) 0%, transparent 50%),
                                   radial-gradient(circle at 40% 60%, rgba(236, 72, 153, 0.2) 0%, transparent 50%),
                                   linear-gradient(45deg, transparent 40%, rgba(139, 92, 246, 0.1) 50%, transparent 60%)`
                }}
              ></div>
              
              {/* Effet de cercles/anneaux futuristes - plus visibles */}
              <div className="absolute inset-0 opacity-60">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="w-96 h-96 rounded-full border-2 border-purple-500/30 animate-pulse"></div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 rounded-full border border-pink-400/40 animate-pulse" style={{animationDelay: '1s'}}></div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full border border-blue-400/30 animate-pulse" style={{animationDelay: '2s'}}></div>
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full border-2 border-purple-400/50 animate-pulse" style={{animationDelay: '3s'}}></div>
                </div>
              </div>
              
              {/* Effet de lignes radiales */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-px h-full bg-gradient-to-t from-transparent via-purple-400/30 to-transparent"
                      style={{
                        transform: `rotate(${i * 30}deg)`,
                        transformOrigin: '50% 50%'
                      }}
                    />
                  ))}
                </div>
              </div>
              
              {/* Particules flottantes améliorées */}
              <div className="absolute top-20 left-20 w-3 h-3 bg-pink-400 rounded-full animate-ping opacity-80"></div>
              <div className="absolute top-40 right-32 w-2 h-2 bg-purple-400 rounded-full animate-ping opacity-60" style={{animationDelay: '2s'}}></div>
              <div className="absolute bottom-32 left-40 w-2 h-2 bg-blue-400 rounded-full animate-ping opacity-70" style={{animationDelay: '4s'}}></div>
              <div className="absolute top-60 right-60 w-1 h-1 bg-pink-300 rounded-full animate-ping opacity-50" style={{animationDelay: '6s'}}></div>
              
              {/* Gradient overlay principal */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/30"></div>
            </div>
          </div>
          
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-xl">
                <h1 className="text-5xl md:text-6xl font-bold mb-6">
                  {featuredVideo.title}
                </h1>
                
                <div className="flex items-center space-x-4 mb-6 text-lg">
                  {featuredVideo.release_year && <span>{featuredVideo.release_year}</span>}
                  {featuredVideo.duration_seconds && (
                    <>
                      <span>•</span>
                      <span>{formatDuration(featuredVideo.duration_seconds)}</span>
                    </>
                  )}
                  <span>•</span>
                  <span className={`${
                    featuredVideo.status === 'published' ? 'text-green-400' : 'text-yellow-400'
                  }`}>
                    {featuredVideo.status === 'published' ? 'Disponible' : 'En traitement'}
                  </span>
                </div>

                {featuredVideo.description && (
                  <p className="text-lg text-gray-300 mb-8 line-clamp-3">
                    {featuredVideo.description}
                  </p>
                )}

                {featuredVideo.created_by && (
                  <div className="flex items-center space-x-2 mb-8 text-purple-400">
                    <Sparkles className="h-5 w-5" />
                    <span className="text-lg">
                      Créé par {featuredVideo.created_by}
                    </span>
                  </div>
                )}

                <div className="flex space-x-4">
                  <button
                    onClick={() => watchVideo(featuredVideo)}
                    disabled={!featuredVideo.playback_url}
                    className={`px-8 py-4 rounded-lg text-lg font-semibold flex items-center space-x-3 ${
                      featuredVideo.playback_url
                        ? 'bg-pink-600 hover:bg-pink-700 text-white' 
                        : 'bg-gray-600 text-gray-300 cursor-not-allowed'
                    }`}
                  >
                    <Play className="h-6 w-6" />
                    <span>
                      {featuredVideo.playback_url ? 'Regarder maintenant' : 'Pas encore disponible'}
                    </span>
                  </button>
                  
                  <button
                    onClick={() => viewDetails(featuredVideo)}
                    className="bg-gray-800/80 hover:bg-gray-700/80 text-white px-8 py-4 rounded-lg text-lg font-semibold flex items-center space-x-3 backdrop-blur-sm"
                  >
                    <Info className="h-6 w-6" />
                    <span>Plus d'infos</span>
                  </button>

                  <WatchlistButton videoId={featuredVideo.id} />
                </div>
              </div>
            </div>
          </div>
          
          {/* Badge "VRAIES DONNÉES" */}
          <div className="absolute top-4 right-4 bg-green-600/90 backdrop-blur-sm px-4 py-2 rounded-lg">
            <span className="text-white font-semibold text-sm">
              ✅ VRAIES DONNÉES SUPABASE
            </span>
          </div>
        </div>
      )}

      {/* Sections de contenu Netflix avec vraies données */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        
        {/* 🔥 Tendances actuelles */}
        <Section
          title="🔥 Tendances actuelles"
          icon={null}
          videos={trendingVideos}
          emptyMessage="Aucune tendance disponible pour le moment"
        />

        {/* 🎬 Films IA populaires */}
        <Section
          title="🎬 Films IA populaires"
          icon={null}
          videos={popularMovies}
          emptyMessage="Aucun film disponible pour le moment"
        />

        {/* 📺 Séries IA exclusives */}
        <Section
          title="📺 Séries IA exclusives"
          icon={null}
          videos={exclusiveSeries}
          emptyMessage="Aucune série disponible pour le moment"
        />

        {/* ✨ Nouveautés */}
        <Section
          title="✨ Nouveautés"
          icon={null}
          videos={displayNewReleases}
          emptyMessage="Aucune nouveauté disponible"
        />
      </div>
      
      {/* Footer debug info */}

    </div>
  );
};

export default HomePage;